import type { CSSProperties, ReactNode } from 'react';
import { appLifecycle, appLink, type PluginEntry } from '../../remotes/registry';
import { CornerTicks } from '../../theme/frontDoor/CornerTicks';
import { FdLink } from '../../theme/frontDoor/FdLink';
import { appIcon } from './appIcons';
import {
  UNCATEGORIZED_COLORS,
  categoryById,
  lifecycleById,
  type AppCategoryId,
  type AppLifecycleId,
} from './appStoreVocabulary';
import { LAUNCHER_COPY as C } from './launcherCopy';
import { monogram } from './launcherModel';
import { useTrackClick } from '../../usage/track';

export interface LauncherTileProps {
  name: string;
  /**
   * An in-shell path starting with "/" ("/workspan") when `external` is false, or an
   * absolute https:// URL when `external` is true. An in-shell href goes through the
   * router, so an absolute URL with `external` false would be routed, not opened.
   */
  href: string;
  /**
   * Opens `href` in a new tab with rel="noopener noreferrer" and shows the "Opens in
   * new tab ↗" badge. Must be true for any URL that leaves the shell.
   */
  external: boolean;
  category?: AppCategoryId | undefined;
  /** Text shown on the tile. Falls back to "No description provided." when absent. */
  blurb?: string | undefined;
  ownerTeam?: string | undefined;
  /** Shows the "AI assistant" badge. */
  ai?: boolean | undefined;
  /** The stage stated beside the category (ADR 0043). Unset or unknown states none. */
  lifecycle?: AppLifecycleId | undefined;
  /** A drawn icon (appIcons.tsx) shown in the mark box instead of the monogram. */
  icon?: ReactNode | undefined;
  /** Runs on a click, before the link opens. It never cancels the navigation. */
  onClick?: (() => void) | undefined;
}

/**
 * A category's colors as the CSS custom properties launcher.css reads, so the
 * stylesheet can switch the text shade in dark mode. An unknown or missing
 * category is Deloitte Green.
 */
export function categoryStyle(category: AppCategoryId | undefined): CSSProperties {
  const colors = categoryById(category) ?? UNCATEGORIZED_COLORS;
  return {
    '--gp-fd-accent': colors.bar,
    '--gp-fd-accent-text': colors.text,
    '--gp-fd-accent-text-dark': colors.textDark,
  } as CSSProperties;
}

/**
 * One launcher tile, presentational: the whole card is a single link. It knows
 * nothing about the registry, so the launcher can reuse it for tiles that aren't
 * registry entries.
 *
 * The color comes from the category (spec 2026-09-30-app-store-categories), through
 * categoryStyle. An unknown or missing category renders as Deloitte Green, with no label.
 * The stage (ADR 0043) sits on the same line as the category, opposite it.
 */
export function LauncherTile({
  name,
  href,
  external,
  category,
  blurb,
  ownerTeam,
  ai,
  lifecycle,
  icon,
  onClick,
}: LauncherTileProps) {
  const cat = categoryById(category);
  const stage = lifecycleById(lifecycle);
  const style = categoryStyle(category);

  // The category and stage follow the name in the DOM so the link's accessible name
  // starts with the app's name; launcher.css (`order: -1`) still shows them on top.
  const body: ReactNode = (
    <>
      <span className="gp-fd-tile-head">
        <span className="gp-fd-mark" aria-hidden="true">
          {icon ?? monogram(name)}
        </span>
        <span className="gp-fd-tile-name">{name}</span>
      </span>
      {(cat || stage) && (
        <span className="gp-fd-tile-meta">
          {cat && <span className="gp-fd-tile-category">{cat.label}</span>}
          {stage && (
            <span
              className={`gp-fd-tile-stage gp-fd-stage-${stage.id}`}
              style={
                {
                  '--gp-fd-stage-dot': stage.dot,
                  '--gp-fd-stage-text': stage.text,
                  '--gp-fd-stage-text-dark': stage.textDark,
                } as CSSProperties
              }
            >
              {stage.label}
            </span>
          )}
        </span>
      )}
      <span className="gp-fd-tile-desc">{blurb ?? 'No description provided.'}</span>
      <span className="gp-fd-tile-footer">
        {ownerTeam && <span className="gp-fd-tile-owner">{ownerTeam}</span>}
        <span className="gp-fd-tile-badges">
          {ai && <span className="gp-fd-badge">AI assistant</span>}
          {external && <span className="gp-fd-badge gp-fd-badge-muted">Opens in new tab ↗</span>}
        </span>
      </span>
    </>
  );

  if (external) {
    return (
      <a className="gp-fd-tile" href={href} target="_blank" rel="noopener noreferrer" style={style} onClick={onClick}>
        {body}
      </a>
    );
  }
  return (
    <FdLink to={href} className="gp-fd-tile" style={style} onClick={onClick}>
      {body}
    </FdLink>
  );
}

/**
 * Trailing in-grid callout tile ("Your app next") at the end of the apps grid,
 * turning trailing grid whitespace into an onboarding invitation (ADR 0028).
 */
export function NextAppTile() {
  return (
    <FdLink to="/onboard" className="gp-fd-tile gp-fd-tile-next">
      <CornerTicks />
      <span className="gp-fd-tile-head">
        <span className="gp-fd-mark gp-fd-mark-next" aria-hidden="true">
          +
        </span>
        <span className="gp-fd-tile-name">{C.nextTileTitle}</span>
      </span>
      <span className="gp-fd-tile-category">{C.nextTileKicker}</span>
      <span className="gp-fd-tile-desc">{C.nextTileBody}</span>
      <span className="gp-fd-tile-footer">
        <span className="gp-fd-tile-cta" aria-hidden="true">
          <span>{C.nextTileCta}</span>
          <span className="gp-fd-tile-arrow">→</span>
        </span>
      </span>
    </FdLink>
  );
}

export interface AppTileProps {
  app: PluginEntry;
}

/**
 * An App Store tile for a registry entry. It only maps the entry onto LauncherTile.
 * A hosted app routes within the shell; a linked app opens its externalUrl in a new
 * tab, and its click is recorded here since no shell route sees it.
 */
export function AppTile({ app }: AppTileProps) {
  const { href, external: linked } = appLink(app);
  const track = useTrackClick();
  return (
    <LauncherTile
      name={app.displayName}
      href={href}
      external={linked}
      category={app.category}
      blurb={app.tagline ?? app.description}
      ownerTeam={app.ownerTeam}
      ai={app.aiEnabled}
      lifecycle={appLifecycle(app)}
      icon={appIcon(app.appId)}
      // A hosted app's open is recorded by RemoteRoute (for the server and for Quick
      // Launch alike); only a linked app leaves the shell.
      onClick={linked ? () => track(`app:${app.appId}`) : undefined}
    />
  );
}

/** Placeholder tile while the registry read is in flight. */
export function AppTileSkeleton() {
  return (
    <div className="gp-fd-tile gp-fd-tile-skeleton" aria-hidden="true">
      <span className="gp-fd-tile-head">
        <span className="gp-fd-mark" />
        <span className="gp-fd-skeleton-bar" style={{ width: '55%' }} />
      </span>
      <span className="gp-fd-skeleton-bar" style={{ width: '92%', marginTop: 6 }} />
      <span className="gp-fd-skeleton-bar" style={{ width: '70%', marginTop: 10 }} />
    </div>
  );
}
